import interstellarApi from './interstellarApi';

/** Payload for POST /infrastructure/freeswitch in Interstellar */
export interface ProvisionFreeswitchPayload {
  cosmos_account_uuid: string;
  client_name: string;
  instance_type?: string;
}

export interface ProvisionFreeswitchResponse {
  stack_id: string;
  stack_name: string;
  status: string;
  region: string;
  console_url: string;
}

export interface FreeswitchStackStatus {
  stack_id: string;
  stack_name: string;
  status: string;
  status_reason?: string | null;
  creation_time?: string | null;
  in_progress: boolean;
  rolling_back: boolean;
  succeeded: boolean;
  failed: boolean;
  outputs: Record<string, string>;
  instance_id?: string | null;
  /** Private IP of the instance, from the template's `InstancePrivateIp` output. */
  instance_private_ip?: string | null;
  efs_access_point_id?: string | null;
  mounted_efs?: string | null;
  failure_reasons: string[];
  console_url: string;
}

/**
 * Describes the generated database. The password is not exposed: `POST /account/`
 * receives `freeswitch_client_name` and reads it from Secrets Manager server-side.
 */
export interface FreeswitchStackCredentials {
  secret_name: string;
  username: string;
  host: string;
  port: number;
  database: string;
}

/**
 * Starts the CloudFormation deployment of a FreeSWITCH instance.
 * @see interstellar/services/infrastructure_service/endpoints.py — `create_freeswitch_stack`
 */
export const provisionFreeswitchStack = async (
  payload: ProvisionFreeswitchPayload
): Promise<ProvisionFreeswitchResponse> => {
  const response = await interstellarApi.post<ProvisionFreeswitchResponse>(
    '/infrastructure/freeswitch',
    payload
  );
  return response.data;
};

/** Reads the current status and outputs of a FreeSWITCH stack. */
export const fetchFreeswitchStackStatus = async (
  stackName: string,
  signal?: AbortSignal
): Promise<FreeswitchStackStatus> => {
  const response = await interstellarApi.get<FreeswitchStackStatus>('/infrastructure/freeswitch', {
    params: { stack_name: stackName },
    signal,
  });
  return response.data;
};

/** Describes the PostgreSQL database the stack created, without its password. */
export const fetchFreeswitchStackCredentials = async (
  clientName: string,
  signal?: AbortSignal
): Promise<FreeswitchStackCredentials> => {
  const response = await interstellarApi.get<FreeswitchStackCredentials>(
    '/infrastructure/freeswitch/credentials',
    { params: { client_name: clientName }, signal }
  );
  return response.data;
};

/** 409 body from `POST /infrastructure/freeswitch`, carrying the name of the existing stack. */
export interface FreeswitchStackConflict {
  message: string;
  stack_name: string;
  stack_id?: string | null;
}

/** Response from GET /infrastructure/freeswitch/preview-delete */
export interface PreviewDeleteFreeswitchResponse {
  account_id: number;
  cosmos_account_id: string;
  client_name: string;
  stack_name: string;
  stack_status: string;
  instance_id: string | null;
  instance_private_ip: string | null;
  creation_time: string | null;
  console_url: string;
  warning: string;
}

/**
 * Previews what would be deleted if the FreeSWITCH infrastructure for an account is removed.
 * Returns details about the stack and instance that would be affected.
 */
export const previewDeleteFreeswitchStack = async (
  accountId: number,
  signal?: AbortSignal
): Promise<PreviewDeleteFreeswitchResponse> => {
  const response = await interstellarApi.get<PreviewDeleteFreeswitchResponse>(
    '/infrastructure/freeswitch/preview-delete',
    { params: { account_id: accountId }, signal }
  );
  return response.data;
};

/** Response from DELETE /infrastructure/freeswitch */
export interface DeleteFreeswitchStackResponse {
  account_id: number;
  stack_name: string;
  status: string;
  instance_id: string | null;
  console_url: string;
}

/**
 * Deletes the FreeSWITCH infrastructure for an account.
 * @see interstellar/services/infrastructure_service/endpoints.py — `delete_freeswitch_stack`
 */
export const deleteFreeswitchStack = async (
  accountId: number
): Promise<DeleteFreeswitchStackResponse> => {
  const response = await interstellarApi.delete<DeleteFreeswitchStackResponse>(
    '/infrastructure/freeswitch',
    { params: { account_id: accountId } }
  );
  return response.data;
};

export const STACK_POLL_INTERVAL_MS = 15000;
/** Creation takes ~8 min and the instance's CreationPolicy alone allows 20; give it 30. */
export const STACK_POLL_TIMEOUT_MS = 30 * 60 * 1000;

export class StackPollAbortedError extends Error {
  constructor() {
    super('Seguimiento del stack cancelado');
    // Restores the prototype chain lost when extending Error down-levelled to ES5.
    Object.setPrototypeOf(this, StackPollAbortedError.prototype);
    this.name = 'StackPollAbortedError';
  }
}

const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  if (signal?.aborted) return Promise.reject(new StackPollAbortedError());

  let detach = (): void => undefined;
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new StackPollAbortedError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    detach = () => signal?.removeEventListener('abort', onAbort);
  }).finally(() => detach());
};

/**
 * Polls the stack until it reaches a terminal state, reporting each intermediate status.
 * Rejects when the deployment fails, the timeout elapses or `signal` is aborted.
 */
export const waitForFreeswitchStack = async (
  stackName: string,
  onStatus?: (status: FreeswitchStackStatus) => void,
  {
    intervalMs = STACK_POLL_INTERVAL_MS,
    timeoutMs = STACK_POLL_TIMEOUT_MS,
    signal,
  }: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
): Promise<FreeswitchStackStatus> => {
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    if (signal?.aborted) throw new StackPollAbortedError();

    const status = await fetchFreeswitchStackStatus(stackName, signal);
    onStatus?.(status);

    if (status.succeeded) return status;
    if (status.failed) {
      const reason = status.failure_reasons[0] || status.status_reason || status.status;
      throw new Error(`El stack ${stackName} fallo: ${reason}`);
    }
    if (Date.now() >= deadline) {
      throw new Error(
        `El stack ${stackName} sigue en ${status.status} tras el tiempo de espera; revisa la consola de AWS.`
      );
    }

    await sleep(intervalMs, signal);
  }
};
